/**
 * PostEngagementPanel Component
 *
 * Shown on a published post to demonstrate `pages_read_engagement` (reaction/
 * comment/share counts) and `read_insights` (impressions/reach) — Flow 6 of
 * the Facebook App Review reviewer instructions.
 */
import { useState, useEffect, useCallback } from 'react';
import { Heart, MessageCircle, Share2, Eye, TrendingUp, RefreshCw, Loader2, AlertCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { social } from '@/services/api/admin';

const formatNumber = (num) => {
  if (num === null || num === undefined) return null;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const Metric = ({ icon: Icon, label, value, isInsight }) => (
  <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
    <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
    {value === null ? (
      <span className="text-xs text-gray-400 dark:text-gray-500 italic">
        {isInsight ? 'Not yet available' : '—'}
      </span>
    ) : (
      <span className="text-lg font-semibold text-gray-900 dark:text-white">{value}</span>
    )}
    <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
  </div>
);

const PostEngagementPanel = ({ postId }) => {
  const [engagement, setEngagement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEngagement = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await social.getPostEngagement(postId);
      setEngagement(data.engagement);
    } catch (err) {
      setError(err.message || 'Failed to load engagement metrics');
    } finally {
      setIsLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchEngagement();
  }, [fetchEngagement]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Engagement</CardTitle>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={fetchEngagement}
            disabled={isLoading}
            aria-label="Refresh"
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && !engagement ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading engagement…</p>
        ) : error ? (
          <div className="flex flex-col items-start gap-2 text-sm text-red-600 dark:text-red-400">
            <div className="flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
            <Button variant="outline" size="sm" onClick={fetchEngagement}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-2">
            <Metric icon={Heart} label="Likes" value={formatNumber(engagement.likes) ?? '0'} />
            <Metric icon={MessageCircle} label="Comments" value={formatNumber(engagement.comments) ?? '0'} />
            <Metric icon={Share2} label="Shares" value={formatNumber(engagement.shares) ?? '0'} />
            <Metric icon={Eye} label="Reach" value={formatNumber(engagement.reach)} isInsight />
            <Metric icon={TrendingUp} label="Impressions" value={formatNumber(engagement.impressions)} isInsight />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PostEngagementPanel;
